import type { OrgStructure } from '@commenda/api';
import type { EntityNode, OrgGraph, OwnershipEdge } from './types';

// Positions are stubbed at (0,0); layoutGraph fills them in. Splitting build
// from layout keeps this pure data transform testable without geometry.
export function buildGraph(structure: OrgStructure): OrgGraph {
  const { entities, edges } = structure;

  const linked = new Set<number>();
  for (const e of edges) {
    linked.add(e.fromCorporationId);
    linked.add(e.toCorporationId);
  }

  const nodes: EntityNode[] = entities.map((e) => ({
    id: String(e.id),
    type: 'entity',
    position: { x: 0, y: 0 },
    data: {
      legalName: e.legalName,
      incorporationCountry: e.incorporationCountry,
      businessEntityType: e.businessEntityType,
      keyPersons: e.keyPersons,
      bankAccounts: e.bankAccounts,
      taxClassification: e.taxClassification,
      taxChartClass: e.taxChartClass,
      taxChartClassOverride: e.taxChartClassOverride,
      isNonControlledEntity: e.isNonControlledEntity,
    },
  }));

  // A parent can hold several share classes of one child, so the same
  // (from, to) pair may appear more than once. Each shareholding gets its own
  // edge id (suffixed by stock class, falling back to its position in the
  // response) — a shared id would make xyflow/React collapse them.
  const edgeIds = edges.map(
    (e, i) =>
      `${e.fromCorporationId}->${e.toCorporationId}#${e.stockClassId ?? i}`,
  );

  // Per-target index of the incoming edges, ordered by source id, so the
  // chips on edges converging on one child can be spread along their curves.
  const inEdgesByTarget = new Map<number, number[]>();
  // Per-(from, to) lanes so parallel shareholdings fan out side by side
  // instead of drawing on top of each other.
  const parallelByPair = new Map<string, number[]>();
  edges.forEach((e, i) => {
    const inList = inEdgesByTarget.get(e.toCorporationId) ?? [];
    inList.push(i);
    inEdgesByTarget.set(e.toCorporationId, inList);

    const pair = `${e.fromCorporationId}->${e.toCorporationId}`;
    const pairList = parallelByPair.get(pair) ?? [];
    pairList.push(i);
    parallelByPair.set(pair, pairList);
  });
  const inIndexFor = new Map<string, number>();
  const parallelFor = new Map<string, { index: number; count: number }>();
  // forEach (vs `for..of`) so the enterprise app's tsconfig
  // (no --downlevelIteration) doesn't choke on Map iteration.
  inEdgesByTarget.forEach((list) => {
    list
      .slice()
      .sort((a, b) => edges[a].fromCorporationId - edges[b].fromCorporationId)
      .forEach((edgeIdx, i) => {
        inIndexFor.set(edgeIds[edgeIdx], i);
      });
  });
  parallelByPair.forEach((list) => {
    list.forEach((edgeIdx, i) => {
      parallelFor.set(edgeIds[edgeIdx], { index: i, count: list.length });
    });
  });

  const flowEdges: OwnershipEdge[] = edges.map((e, i) => {
    // Prefer diluted (% of target's total authorized shares). Falls back to
    // raw per-class % during the BE rollout window — once every environment
    // returns dilutedOwnershipPercent, the `??` can be dropped.
    const percent = e.dilutedOwnershipPercent ?? e.ownershipPercent;
    // Spec: control-only = 0% economic AND HSC=true. Any positive percent is
    // economic regardless of HSC.
    const kind: 'economic' | 'control-only' =
      percent === 0 && e.hasSignificantControl ? 'control-only' : 'economic';

    const id = edgeIds[i];
    const parallel = parallelFor.get(id) ?? { index: 0, count: 1 };
    return {
      id,
      source: String(e.fromCorporationId),
      target: String(e.toCorporationId),
      type: 'ownership',
      data: {
        percent,
        kind,
        inIndex: inIndexFor.get(id) ?? 0,
        parallelIndex: parallel.index,
        parallelCount: parallel.count,
      },
    };
  });

  // Workspaces with no ownership edges yet (typically a fresh single-entity
  // workspace) show every entity on the canvas — otherwise the chart would
  // be empty and the entity would only surface in the unlinked side-panel.
  if (edges.length === 0) {
    return { nodes, edges: flowEdges, unlinked: [] };
  }

  // Unlinked entities surface in the side panel; only linked nodes hit the canvas.
  const linkedNodes = nodes.filter((n) => linked.has(Number(n.id)));
  const unlinked = nodes.filter((n) => !linked.has(Number(n.id)));

  return { nodes: linkedNodes, edges: flowEdges, unlinked };
}
